// ChannelForwardModal.js
import React, { useState, useEffect } from 'react';
import './ChannelForwardModal.css';
import { toast } from 'react-hot-toast';
import API_URL from '../../../config';

const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'İngilizce', flag: '🇬🇧' },
    { code: 'ru', name: 'Rusça', flag: '🇷🇺' },
    { code: 'ar', name: 'Arapça', flag: '🇸🇦' },
    { code: 'fa', name: 'Farsça', flag: '🇮🇷' },
    { code: 'de', name: 'Almanca', flag: '🇩🇪' },
    { code: 'fr', name: 'Fransızca', flag: '🇫🇷' },
    { code: 'es', name: 'İspanyolca', flag: '🇪🇸' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
];

const IMAGE_ASPECTS = [
    { value: 'square', label: 'Kare', icon: '⬜', desc: '1:1' },
    { value: '9:16', label: 'Dikey', icon: '📱', desc: '9:16' },
    { value: '16:9', label: 'Yatay', icon: '🖥️', desc: '16:9' }
];

function ChannelForwardModal({ isOpen, onClose, channel, onUpdate }) {
    const [forwards, setForwards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    // Yeni kaynak ekleme state'leri
    const [newSource, setNewSource] = useState('');
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [addingSource, setAddingSource] = useState(false);

    // Yeni kaynak ayarları
    const [newSettings, setNewSettings] = useState({
        asForward: false,
        forwardText: true,
        forwardPhoto: true,
        forwardVideo: true,
        forwardDocument: true,
        forwardAudio: true,
        translateEnabled: false,
        translateLanguage: '',
        imageGenEnabled: false,
        imageGenAspect: 'square'
    });

    // Modal açıldığında forward'ları yükle
    useEffect(() => {
        if (isOpen && channel?.id) {
            fetchForwards();
        }
    }, [isOpen, channel?.id]);

    // Modal kapandığında state'leri sıfırla
    useEffect(() => {
        if (!isOpen) {
            setNewSource('');
            setValidationResult(null);
            setNewSettings({
                asForward: false,
                forwardText: true,
                forwardPhoto: true,
                forwardVideo: true,
                forwardDocument: true,
                forwardAudio: true,
                translateEnabled: false,
                translateLanguage: '',
                imageGenEnabled: false,
                imageGenAspect: 'square'
            });
        }
    }, [isOpen]);

    // Forward'ları getir
    const fetchForwards = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/channel-forwards/channel/${channel.id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setForwards(data.forwards || []);
            }
        } catch (error) {
            console.error('Forward listesi hatası:', error);
            toast.error('Forward listesi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    // Kaynak kanalı doğrula
    const handleValidate = async () => {
        if (!newSource.trim()) {
            toast.error('Kaynak kanal username girin');
            return;
        }

        setValidating(true);
        setValidationResult(null);

        try {
            const response = await fetch(`${API_URL}/channel-forwards/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ username: newSource })
            });

            const data = await response.json();
            setValidationResult(data);

            if (!data.success && !data.valid) {
                toast.error(data.error || 'Kanal doğrulanamadı');
            }
        } catch (error) {
            console.error('Doğrulama hatası:', error);
            toast.error('Doğrulama başarısız');
            setValidationResult({ valid: false, error: error.message });
        } finally {
            setValidating(false);
        }
    };

    // Yeni kaynak ekle
    const handleAddSource = async () => {
        if (!validationResult?.valid) {
            toast.error('Önce kanalı doğrulayın');
            return;
        }

        setAddingSource(true);

        try {
            const response = await fetch(`${API_URL}/channel-forwards`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    targetChannelId: channel.id,
                    sourceUsername: newSource,
                    ...newSettings
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || 'Kaynak eklendi');
                setNewSource('');
                setValidationResult(null);
                setNewSettings({
                    asForward: false,
                    forwardText: true,
                    forwardPhoto: true,
                    forwardVideo: true,
                    forwardDocument: true,
                    forwardAudio: true,
                    translateEnabled: false,
                    translateLanguage: '',
                    imageGenEnabled: false,
                    imageGenAspect: 'square'
                });
                fetchForwards();
            } else {
                toast.error(data.error || 'Ekleme başarısız');
            }
        } catch (error) {
            console.error('Ekleme hatası:', error);
            toast.error('Kaynak eklenemedi');
        } finally {
            setAddingSource(false);
        }
    };

    // Forward'ı başlat
    const handleStart = async (forwardId) => {
        setActionLoading(forwardId);
        try {
            const response = await fetch(`${API_URL}/channel-forwards/${forwardId}/start`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || 'Başlatıldı');
                fetchForwards();
            } else {
                toast.error(data.error || 'Başlatılamadı');
            }
        } catch (error) {
            console.error('Başlatma hatası:', error);
            toast.error('Başlatma başarısız');
        } finally {
            setActionLoading(null);
        }
    };

    // Forward'ı durdur
    const handleStop = async (forwardId) => {
        setActionLoading(forwardId);
        try {
            const response = await fetch(`${API_URL}/channel-forwards/${forwardId}/stop`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                toast.success(data.message || 'Durduruldu');
                fetchForwards();
            } else {
                toast.error(data.error || 'Durdurulamadı');
            }
        } catch (error) {
            console.error('Durdurma hatası:', error);
            toast.error('Durdurma başarısız');
        } finally {
            setActionLoading(null);
        }
    };

    // Forward'ı sil
    const handleDelete = async (forwardId, sourceUsername) => {
        if (!window.confirm(`"@${sourceUsername}" kaynağını silmek istediğinize emin misiniz?`)) {
            return;
        }

        setActionLoading(forwardId);
        try {
            const response = await fetch(`${API_URL}/channel-forwards/${forwardId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Kaynak silindi');
                fetchForwards();
            } else {
                toast.error(data.error || 'Silinemedi');
            }
        } catch (error) {
            console.error('Silme hatası:', error);
            toast.error('Silme başarısız');
        } finally {
            setActionLoading(null);
        }
    };

    // Forward ayarlarını güncelle
    const handleUpdateSettings = async (forwardId, settings) => {
        try {
            const response = await fetch(`${API_URL}/channel-forwards/${forwardId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(settings)
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Ayarlar güncellendi');
                fetchForwards();
            } else {
                toast.error(data.error || 'Güncelleme başarısız');
            }
        } catch (error) {
            console.error('Güncelleme hatası:', error);
            toast.error('Güncelleme başarısız');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="cfm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="cfm-modal-container">
                {/* Header */}
                <div className="cfm-modal-header">
                    <h2>
                        <span className="cfm-header-icon">📡</span>
                        Otomatik Paylaşım
                        <span className="cfm-channel-name">— {channel?.name}</span>
                    </h2>
                    <button className="cfm-modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Body */}
                <div className="cfm-modal-body">
                    {/* Mevcut Kaynaklar */}
                    <div className="cfm-section">
                        <h3 className="cfm-section-title">
                            <span>📺</span> Kaynak Kanallar
                            <span className="cfm-count-badge">{forwards.length}</span>
                        </h3>

                        {loading ? (
                            <div className="cfm-loading">
                                <div className="cfm-spinner"></div>
                                <span>Yükleniyor...</span>
                            </div>
                        ) : forwards.length === 0 ? (
                            <div className="cfm-empty">
                                <span className="cfm-empty-icon">📭</span>
                                <p>Henüz kaynak kanal eklenmemiş</p>
                                <small>Aşağıdan yeni kaynak ekleyebilirsiniz</small>
                            </div>
                        ) : (
                            <div className="cfm-forwards-list">
                                {forwards.map(forward => (
                                    <div key={forward.id} className={`cfm-forward-item ${forward.is_active ? 'active' : 'inactive'}`}>
                                        <div className="cfm-forward-header">
                                            <div className="cfm-forward-info">
                                                <span className="cfm-forward-username">@{forward.source_username}</span>
                                                {forward.source_title && (
                                                    <span className="cfm-forward-title">{forward.source_title}</span>
                                                )}
                                            </div>
                                            <div className="cfm-forward-status">
                                                {forward.is_active ? (
                                                    <span className="cfm-status-badge active">🟢 Aktif</span>
                                                ) : (
                                                    <span className="cfm-status-badge inactive">🔴 Pasif</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="cfm-forward-details">
                                            <div className="cfm-forward-mode">
                                                <span className={`cfm-mode-badge ${forward.as_forward ? 'forward' : 'copy'}`}>
                                                    {forward.as_forward ? '📨 İletildi' : '📋 Kopyala'}
                                                </span>
                                            </div>
                                            <div className="cfm-forward-filters">
                                                {forward.forward_text === 1 && <span className="cfm-filter-tag">📝</span>}
                                                {forward.forward_photo === 1 && <span className="cfm-filter-tag">🖼️</span>}
                                                {forward.forward_video === 1 && <span className="cfm-filter-tag">🎬</span>}
                                                {forward.forward_document === 1 && <span className="cfm-filter-tag">📎</span>}
                                                {forward.forward_audio === 1 && <span className="cfm-filter-tag">🎵</span>}
                                            </div>
                                            {forward.translate_enabled === 1 && forward.translate_language && (
                                                <span className="cfm-translate-badge">
                                                    🌐 {SUPPORTED_LANGUAGES.find(l => l.code === forward.translate_language)?.flag} {SUPPORTED_LANGUAGES.find(l => l.code === forward.translate_language)?.name}
                                                </span>
                                            )}
                                            {forward.image_gen_enabled === 1 && (
                                                <span className="cfm-imagen-badge">
                                                    🎨 Görsel {IMAGE_ASPECTS.find(a => a.value === forward.image_gen_aspect)?.desc || '1:1'}
                                                </span>
                                            )}
                                            <div className="cfm-forward-stats">
                                                <span className="cfm-stat">📊 {forward.total_forwarded || 0} iletildi</span>
                                            </div>
                                        </div>

                                        <div className="cfm-forward-actions">
                                            {/* Mod değiştirme */}
                                            <button
                                                className={`cfm-action-btn cfm-mode-toggle ${forward.as_forward ? 'is-forward' : 'is-copy'}`}
                                                onClick={() => handleUpdateSettings(forward.id, { asForward: !forward.as_forward })}
                                                title={forward.as_forward ? 'Kopyala moduna geç' : 'İletildi moduna geç'}
                                            >
                                                {forward.as_forward ? '📋' : '📨'}
                                            </button>

                                            {/* Çeviri toggle */}
                                            <button
                                                className={`cfm-action-btn cfm-translate-toggle ${forward.translate_enabled ? 'is-active' : ''}`}
                                                onClick={() => handleUpdateSettings(forward.id, {
                                                    translateEnabled: !forward.translate_enabled,
                                                    translateLanguage: !forward.translate_enabled ? (forward.translate_language || 'tr') : forward.translate_language
                                                })}
                                                title={forward.translate_enabled ? 'Çeviriyi kapat' : 'Çeviriyi aç'}
                                            >
                                                🌐
                                            </button>

                                            {/* Dil seçici - çeviri açıksa göster */}
                                            {forward.translate_enabled === 1 && (
                                                <select
                                                    className="cfm-lang-select"
                                                    value={forward.translate_language || 'tr'}
                                                    onChange={(e) => handleUpdateSettings(forward.id, { translateLanguage: e.target.value })}
                                                >
                                                    {SUPPORTED_LANGUAGES.map(lang => (
                                                        <option key={lang.code} value={lang.code}>
                                                            {lang.flag} {lang.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}

                                            {/* Görsel üretim toggle */}
                                            <button
                                                className={`cfm-action-btn cfm-imagen-toggle ${forward.image_gen_enabled ? 'is-active' : ''}`}
                                                onClick={() => handleUpdateSettings(forward.id, {
                                                    imageGenEnabled: !forward.image_gen_enabled
                                                })}
                                                title={forward.image_gen_enabled ? 'Görsel üretimi kapat' : 'Görsel üretimi aç'}
                                            >
                                                🎨
                                            </button>

                                            {/* Görsel aspect seçici */}
                                            {forward.image_gen_enabled === 1 && (
                                                <select
                                                    className="cfm-aspect-select"
                                                    value={forward.image_gen_aspect || 'square'}
                                                    onChange={(e) => handleUpdateSettings(forward.id, { imageGenAspect: e.target.value })}
                                                >
                                                    {IMAGE_ASPECTS.map(a => (
                                                        <option key={a.value} value={a.value}>{a.icon} {a.label}</option>
                                                    ))}
                                                </select>
                                            )}

                                            {/* Başlat/Durdur */}
                                            {forward.is_active ? (
                                                <button
                                                    className="cfm-action-btn cfm-stop-btn"
                                                    onClick={() => handleStop(forward.id)}
                                                    disabled={actionLoading === forward.id}
                                                    title="Durdur"
                                                >
                                                    {actionLoading === forward.id ? '⏳' : '⏸️'}
                                                </button>
                                            ) : (
                                                <button
                                                    className="cfm-action-btn cfm-start-btn"
                                                    onClick={() => handleStart(forward.id)}
                                                    disabled={actionLoading === forward.id}
                                                    title="Başlat"
                                                >
                                                    {actionLoading === forward.id ? '⏳' : '▶️'}
                                                </button>
                                            )}

                                            {/* Sil */}
                                            <button
                                                className="cfm-action-btn cfm-delete-btn"
                                                onClick={() => handleDelete(forward.id, forward.source_username)}
                                                disabled={actionLoading === forward.id}
                                                title="Sil"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Yeni Kaynak Ekleme */}
                    <div className="cfm-section cfm-add-section">
                        <h3 className="cfm-section-title">
                            <span>➕</span> Yeni Kaynak Ekle
                        </h3>

                        <div className="cfm-add-form">
                            {/* Username Input */}
                            <div className="cfm-input-group">
                                <label>Kaynak Kanal Username</label>
                                <div className="cfm-input-row">
                                    <div className="cfm-input-wrapper">
                                        <span className="cfm-input-prefix">@</span>
                                        <input
                                            type="text"
                                            value={newSource}
                                            onChange={(e) => {
                                                setNewSource(e.target.value.replace('@', ''));
                                                setValidationResult(null);
                                            }}
                                            placeholder="kanal_username"
                                            className="cfm-input"
                                        />
                                    </div>
                                    <button
                                        className="cfm-validate-btn"
                                        onClick={handleValidate}
                                        disabled={validating || !newSource.trim()}
                                    >
                                        {validating ? (
                                            <><span className="cfm-btn-spinner"></span> Kontrol</>
                                        ) : (
                                            '✓ Doğrula'
                                        )}
                                    </button>
                                </div>

                                {/* Doğrulama Sonucu */}
                                {validationResult && (
                                    <div className={`cfm-validation-result ${validationResult.valid ? 'valid' : 'invalid'}`}>
                                        {validationResult.valid ? (
                                            <>
                                                <span className="cfm-valid-icon">✅</span>
                                                <div className="cfm-valid-info">
                                                    <strong>{validationResult.title}</strong>
                                                    {validationResult.participantsCount && (
                                                        <span className="cfm-participant-count">
                                                            👥 {validationResult.participantsCount.toLocaleString('tr-TR')} üye
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="cfm-invalid-icon">❌</span>
                                                <span>{validationResult.error}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Ayarlar - Sadece doğrulama başarılıysa göster */}
                            {validationResult?.valid && (
                                <>
                                    {/* Paylaşım Modu */}
                                    <div className="cfm-input-group">
                                        <label>Paylaşım Modu</label>
                                        <div className="cfm-mode-selector">
                                            <button
                                                className={`cfm-mode-option ${!newSettings.asForward ? 'selected' : ''}`}
                                                onClick={() => setNewSettings({ ...newSettings, asForward: false })}
                                            >
                                                <span className="cfm-mode-icon">📋</span>
                                                <span className="cfm-mode-label">Kopyala</span>
                                                <span className="cfm-mode-desc">İçerik sizden gelmiş gibi görünür</span>
                                            </button>
                                            <button
                                                className={`cfm-mode-option ${newSettings.asForward ? 'selected' : ''}`}
                                                onClick={() => setNewSettings({ ...newSettings, asForward: true })}
                                            >
                                                <span className="cfm-mode-icon">📨</span>
                                                <span className="cfm-mode-label">İletildi Olarak</span>
                                                <span className="cfm-mode-desc">"Forwarded from" etiketi ile</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* İçerik Türleri */}
                                    <div className="cfm-input-group">
                                        <label>İçerik Türleri</label>
                                        <div className="cfm-content-types">
                                            <label className="cfm-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.forwardText}
                                                    onChange={(e) => setNewSettings({ ...newSettings, forwardText: e.target.checked })}
                                                />
                                                <span className="cfm-checkbox-label">📝 Metin</span>
                                            </label>
                                            <label className="cfm-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.forwardPhoto}
                                                    onChange={(e) => setNewSettings({ ...newSettings, forwardPhoto: e.target.checked })}
                                                />
                                                <span className="cfm-checkbox-label">🖼️ Fotoğraf</span>
                                            </label>
                                            <label className="cfm-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.forwardVideo}
                                                    onChange={(e) => setNewSettings({ ...newSettings, forwardVideo: e.target.checked })}
                                                />
                                                <span className="cfm-checkbox-label">🎬 Video</span>
                                            </label>
                                            <label className="cfm-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.forwardDocument}
                                                    onChange={(e) => setNewSettings({ ...newSettings, forwardDocument: e.target.checked })}
                                                />
                                                <span className="cfm-checkbox-label">📎 Dosya</span>
                                            </label>
                                            <label className="cfm-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.forwardAudio}
                                                    onChange={(e) => setNewSettings({ ...newSettings, forwardAudio: e.target.checked })}
                                                />
                                                <span className="cfm-checkbox-label">🎵 Ses</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Çeviri Ayarı */}
                                    <div className="cfm-input-group">
                                        <label>Çeviri</label>
                                        <div className="cfm-translate-section">
                                            <label className="cfm-checkbox cfm-translate-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.translateEnabled}
                                                    onChange={(e) => setNewSettings({
                                                        ...newSettings,
                                                        translateEnabled: e.target.checked,
                                                        translateLanguage: e.target.checked ? (newSettings.translateLanguage || 'tr') : ''
                                                    })}
                                                />
                                                <span className="cfm-checkbox-label">🌐 Otomatik Çeviri</span>
                                            </label>

                                            {newSettings.translateEnabled && (
                                                <div className="cfm-language-selector">
                                                    <label>Hedef Dil</label>
                                                    <div className="cfm-language-grid">
                                                        {SUPPORTED_LANGUAGES.map(lang => (
                                                            <button
                                                                key={lang.code}
                                                                className={`cfm-lang-option ${newSettings.translateLanguage === lang.code ? 'selected' : ''}`}
                                                                onClick={() => setNewSettings({ ...newSettings, translateLanguage: lang.code })}
                                                            >
                                                                <span className="cfm-lang-flag">{lang.flag}</span>
                                                                <span className="cfm-lang-name">{lang.name}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Görsel Üretim Ayarı */}
                                    <div className="cfm-input-group">
                                        <label>Görsel Üretim</label>
                                        <div className="cfm-translate-section">
                                            <label className="cfm-checkbox cfm-translate-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={newSettings.imageGenEnabled}
                                                    onChange={(e) => setNewSettings({
                                                        ...newSettings,
                                                        imageGenEnabled: e.target.checked
                                                    })}
                                                />
                                                <span className="cfm-checkbox-label">🎨 AI Görsel Üret</span>
                                            </label>
                                            <small style={{ color: '#888', fontSize: '11px', marginTop: '-4px' }}>
                                                Sadece metin mesajlara otomatik görsel üretir
                                            </small>

                                            {newSettings.imageGenEnabled && (
                                                <div className="cfm-language-selector">
                                                    <label>Görsel Boyutu</label>
                                                    <div className="cfm-language-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                                                        {IMAGE_ASPECTS.map(aspect => (
                                                            <button
                                                                key={aspect.value}
                                                                className={`cfm-lang-option ${newSettings.imageGenAspect === aspect.value ? 'selected' : ''}`}
                                                                onClick={() => setNewSettings({ ...newSettings, imageGenAspect: aspect.value })}
                                                            >
                                                                <span className="cfm-lang-flag">{aspect.icon}</span>
                                                                <span className="cfm-lang-name">{aspect.label}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Ekle Butonu */}
                                    <button
                                        className="cfm-add-btn"
                                        onClick={handleAddSource}
                                        disabled={addingSource}
                                    >
                                        {addingSource ? (
                                            <><span className="cfm-btn-spinner"></span> Ekleniyor...</>
                                        ) : (
                                            <>➕ Kaynak Ekle</>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Bilgi Kutusu */}
                    <div className="cfm-info-box">
                        <span className="cfm-info-icon">ℹ️</span>
                        <div className="cfm-info-content">
                            <p><strong>Nasıl Çalışır?</strong></p>
                            <ul>
                                <li>Kaynak kanaldaki yeni paylaşımlar otomatik olarak bu kanala aktarılır</li>
                                <li>Sadece <strong>başlattıktan sonra</strong> yapılan paylaşımlar iletilir</li>
                                <li><strong>Kopyala:</strong> İçerik sizden gelmiş gibi görünür</li>
                                <li><strong>İletildi:</strong> Orijinal kaynak gösterilir</li>
                                <li><strong>🎨 Görsel:</strong> Metin mesajlara AI ile otomatik görsel üretir</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="cfm-modal-footer">
                    <button className="cfm-close-btn" onClick={onClose}>
                        Kapat
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChannelForwardModal;